import { useEffect, useState } from 'react'
import { contentCalendarAPI } from '../services/api'
import type { ScheduledContent, ScheduledContentStatus } from '../types'
import toast from 'react-hot-toast'
import { CalendarIcon } from '@heroicons/react/24/outline'
import ConfirmDialog from '../components/ConfirmDialog'
import LoadingScreen from '../components/LoadingScreen'

const STATUS_STYLES: Record<ScheduledContentStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SCHEDULED: 'bg-primary-50 text-primary-700',
  PUBLISHED: 'bg-success-50 text-success-700',
  FAILED: 'bg-danger-50 text-danger-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
}

const formatScheduledAt = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

const Scheduled = () => {
  const [items, setItems] = useState<ScheduledContent[]>([])
  const [loading, setLoading] = useState(true)
  const [cancellingId, setCancellingId] = useState<number | null>(null)
  const [confirmingCancelId, setConfirmingCancelId] = useState<number | null>(null)

  useEffect(() => {
    loadScheduled()
  }, [])

  const loadScheduled = async () => {
    try {
      const data = await contentCalendarAPI.getScheduled({ limit: 50 })
      setItems(data)
    } catch (error) {
      toast.error('Failed to load scheduled content')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    if (confirmingCancelId === null) return

    const id = confirmingCancelId
    setCancellingId(id)
    try {
      const updated = await contentCalendarAPI.cancelScheduled(id)
      setItems((prev) => prev.map((item) => (item.id === id ? updated : item)))
      toast.success('Scheduled post cancelled')
      setConfirmingCancelId(null)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to cancel scheduled post')
    } finally {
      setCancellingId(null)
    }
  }

  if (loading) {
    return <LoadingScreen />
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Scheduled Content</h1>

      {items.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <p className="text-gray-500">
            No scheduled content yet. Generate content on the Create page and use "Schedule for
            Later" to plan your posts.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {items.map((item) => (
            <ScheduledContentCard
              key={item.id}
              item={item}
              cancelling={cancellingId === item.id}
              onCancel={() => setConfirmingCancelId(item.id)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmingCancelId !== null}
        title="Cancel scheduled post"
        message="Are you sure you want to cancel this scheduled post? This action cannot be undone."
        confirmLabel="Cancel Post"
        cancelLabel="Keep It"
        confirming={cancellingId !== null}
        danger
        onConfirm={handleCancel}
        onCancel={() => setConfirmingCancelId(null)}
      />
    </div>
  )
}

interface ScheduledContentCardProps {
  item: ScheduledContent
  cancelling: boolean
  onCancel: () => void
}

const ScheduledContentCard = ({ item, cancelling, onCancel }: ScheduledContentCardProps) => {
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-gray-500 uppercase">{item.content_type}</span>
          <span className={`px-2 py-1 text-xs font-medium rounded ${STATUS_STYLES[item.status]}`}>
            {item.status}
          </span>
        </div>
        <span className="flex items-center gap-1 text-sm text-gray-500">
          <CalendarIcon className="h-4 w-4" />
          {formatScheduledAt(item.scheduled_at)}
        </span>
      </div>

      {item.title && <h3 className="font-semibold text-lg mb-2">{item.title}</h3>}

      <p className="text-gray-700 mb-4 line-clamp-4">{item.body}</p>

      {item.tags && item.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {item.tags.slice(0, 5).map((tag, index) => (
            <span key={index} className="text-xs px-2 py-1 bg-primary-50 text-primary-600 rounded">
              {tag}
            </span>
          ))}
        </div>
      )}

      {item.status === 'FAILED' && item.failure_reason && (
        <p className="text-sm text-danger-700 mb-4">{item.failure_reason}</p>
      )}

      {item.status === 'SCHEDULED' && (
        <div className="flex items-center justify-end pt-4 border-t">
          <button
            onClick={onCancel}
            disabled={cancelling}
            className="px-4 py-2 border border-red-300 text-red-700 text-sm rounded-md hover:bg-red-50 disabled:opacity-50"
          >
            {cancelling ? 'Cancelling...' : 'Cancel'}
          </button>
        </div>
      )}
    </div>
  )
}

export default Scheduled
